// Quick add to cart (QA-001..007) helpers for the linked search card.
import { formatBranchStockLabel, STOCK_COUNT_DISPLAY_CAP } from './branchPriority'
import { formatAvailableWhen, formatDayShortMonth } from './datetime'
import type { BranchStockListing } from './branchPriority'
import type { CartFulfillmentMethod } from './cartFulfillment'

/** A quick-add toast: bold heading plus the product name under it. */
export interface QuickAddConfirmation {
  title: string
  message: string
}

/**
 * Confirmation toast for a quick add (QA-007 AC2): "Added 15 to cart" over
 * the product name.
 *
 * @param addedQuantity - How many were added on this tap.
 * @param productName - Name shown to the engineer.
 */
export function quickAddConfirmation(
  addedQuantity: number,
  productName: string,
): QuickAddConfirmation {
  return { title: `Added ${addedQuantity.toLocaleString()} to cart`, message: productName }
}

/**
 * Which method a linked card starts on (QA-002 AC2/AC3): this product's own
 * cart line at the branch when there is one (adding goes onto that line);
 * else the one the basket already uses at this branch; Collection when the
 * basket has none there or uses both. A method the branch can't serve falls
 * back to the other.
 *
 * @param productLineMethod - Method of this product's line at the branch,
 *   or null when it isn't in the cart there (or the line has none stored).
 * @param basketMethods - Methods the basket's lines at this branch use.
 * @param canCollect - Whether the branch offers collection.
 * @param canDeliver - Whether the branch offers delivery.
 */
export function defaultQuickAddMethod(
  productLineMethod: CartFulfillmentMethod | null,
  basketMethods: ReadonlyArray<CartFulfillmentMethod>,
  canCollect: boolean,
  canDeliver: boolean,
): CartFulfillmentMethod {
  const preferred: CartFulfillmentMethod =
    productLineMethod ?? (basketMethods.length === 1 ? basketMethods[0] : 'pickup')
  if (preferred === 'delivery') return canDeliver || !canCollect ? 'delivery' : 'pickup'
  return canCollect || !canDeliver ? 'pickup' : 'delivery'
}

/** Branch and supplier-warehouse quantities for one method. */
export interface QuickAddMethodStock {
  branchQty: number | null
  depotQty: number | null
  // The most the method can supply without a backorder: the larger of the
  // branch and warehouse figures — the detail page's rule, so the card's
  // "Only X in stock" and backorder point match it.
  capacityQty: number
}

/**
 * A linked card's stock for the selected method. Delivery includes stock a
 * branch delivers from another branch, as the product page and cart count
 * it. A null method figure means none for that method, so it is never filled
 * from the combined figures; only an older response without the per-method
 * split falls back to those.
 *
 * @param listing - The card's listing at its branch.
 * @param method - The selected collection / delivery method.
 */
export function quickAddMethodStock(
  listing: BranchStockListing,
  method: CartFulfillmentMethod,
): QuickAddMethodStock {
  const isPickup = method === 'pickup'
  const branchQty = listing.hasMethodStock
    ? (isPickup ? listing.collectionStock : listing.deliveryStock)
    : listing.branch
  const depotQty = listing.hasMethodStock
    ? (isPickup ? listing.pickupDepotStock : listing.deliveryDepotStock)
    : listing.depot
  return {
    branchQty,
    depotQty,
    capacityQty: Math.max(branchQty ?? 0, depotQty ?? 0, 0),
  }
}

/**
 * Whether the selected method can take a backorder: the supplier / product
 * allows it and a date exists for that method — the detail page's rule.
 *
 * @param listing - The card's listing at its branch.
 * @param method - The selected collection / delivery method.
 */
export function quickAddCanBackorder(
  listing: BranchStockListing,
  method: CartFulfillmentMethod,
): boolean {
  const backorderDate =
    method === 'pickup' ? listing.backorderCollectionDate : listing.backorderDeliveryDate
  return listing.backorderAllowed && backorderDate != null
}

/**
 * Whether the method can be ordered at all: some branch / warehouse stock,
 * or a backorder with a date. When not, the card says "Not available for
 * collection" (the detail page's wording) instead of showing zero stock.
 *
 * @param listing - The card's listing at its branch.
 * @param method - The selected collection / delivery method.
 */
export function quickAddMethodOrderable(
  listing: BranchStockListing,
  method: CartFulfillmentMethod,
): boolean {
  return (
    quickAddMethodStock(listing, method).capacityQty > 0 ||
    quickAddCanBackorder(listing, method)
  )
}

/** "Not available for collection" / "Not available for delivery". */
export function quickAddNotAvailableLabel(method: CartFulfillmentMethod): string {
  return `Not available for ${method === 'pickup' ? 'collection' : 'delivery'}`
}

/**
 * The "Only X in stock" note under the quantity box. With this product
 * already in the basket at this branch, it also says how much is there and
 * how many more fit, in whole packs:
 * "Only 7 in stock. You already have 4 in your basket, so you can add 3 more."
 *
 * @param capacityQty - What the selected method can supply.
 * @param quantityInCart - This product's quantity in the basket at this branch.
 * @param packSize - Multiple the product is sold in (1 = any quantity).
 */
export function quickAddOnlyInStockMessage(
  capacityQty: number,
  quantityInCart: number,
  packSize: number,
): string {
  const stock = `Only ${capacityQty.toLocaleString()} in stock`
  if (quantityInCart <= 0) return stock
  const inBasket = `${stock}. You already have ${quantityInCart.toLocaleString()} in your basket`
  const step = packSize > 1 ? packSize : 1
  // A basket line off the pack size can't take any whole-pack add, so no
  // amount is offered (the button says "Sold in multiples of N").
  const canAdd =
    quantityInCart % step === 0
      ? Math.floor(Math.max(0, capacityQty - quantityInCart) / step) * step
      : 0
  return canAdd > 0
    ? `${inBasket}, so you can add ${canAdd.toLocaleString()} more.`
    : `${inBasket}.`
}

const formatCount = (quantity: number): string =>
  quantity > STOCK_COUNT_DISPLAY_CAP ? `${STOCK_COUNT_DISPLAY_CAP}+` : String(quantity)

/**
 * A linked card's stock line for the selected method (QA-003):
 * "10 available today · 5 available on 18 Oct". Branch stock first, then
 * the supplier warehouse; a source with nothing is left out (AC2), and a
 * source without a date shows its count instead ("5 in supplier
 * warehouse", AC5). The warehouse part is also left out when it repeats
 * the branch figure, as on the product page. Null when neither source has
 * a known figure, so the card shows its plain badge.
 *
 * @param listing - The card's listing at its branch.
 * @param method - The selected collection / delivery method.
 */
export function quickAddStockLabel(
  listing: BranchStockListing,
  method: CartFulfillmentMethod,
): string | null {
  const isPickup = method === 'pickup'
  const { branchQty, depotQty } = quickAddMethodStock(listing, method)
  const branchDate = isPickup ? listing.earliestCollection : listing.earliestDelivery
  const depotDate = isPickup ? listing.pickupDepotDate : listing.deliveryDepotDate

  const parts: string[] = []
  if (branchQty != null && branchQty > 0) {
    const when = formatAvailableWhen(branchDate)
    parts.push(
      when ? `${formatCount(branchQty)} available ${when}` : `${formatCount(branchQty)} in branch`,
    )
  }
  if (depotQty != null && depotQty > 0 && depotQty !== branchQty) {
    const when = formatAvailableWhen(depotDate)
    parts.push(
      when
        ? `${formatCount(depotQty)} available ${when}`
        : `${formatCount(depotQty)} in supplier warehouse`,
    )
  }
  if (parts.length > 0) return parts.join(' · ')
  // Nothing to count for this method: show the zeros rather than "In stock".
  return formatBranchStockLabel(branchQty, depotQty)
}

/**
 * QA-005 backorder warning for a linked card, or null when none applies:
 * "60 will be backordered (est. 18 Oct)". Shown only when the selected
 * method can take a backorder and the quantity is above what the method can
 * supply (as on the detail page); the number is the full quantity (AC2), the date the method's
 * backorder estimate.
 *
 * @param listing - The card's listing at its branch.
 * @param method - The selected collection / delivery method.
 * @param quantity - What the cart line will hold after the add (null while
 *   the box is empty).
 */
export function quickAddBackorderWarning(
  listing: BranchStockListing,
  method: CartFulfillmentMethod,
  quantity: number | null,
): string | null {
  if (quantity === null || quantity <= 0) return null
  if (!quickAddCanBackorder(listing, method)) return null
  if (quantity <= quickAddMethodStock(listing, method).capacityQty) return null
  const estimate = formatDayShortMonth(
    method === 'pickup' ? listing.backorderCollectionDate : listing.backorderDeliveryDate,
  )
  const count = quantity.toLocaleString()
  return estimate
    ? `${count} will be backordered (est. ${estimate})`
    : `${count} will be backordered`
}

/** The fields a linked card reads from the quantity-specific stock check. */
export interface QuickAddQuantityCheckItem {
  product_code?: string
  pickup_dc_date?: string | null
  delivery_dc_date?: string | null
  earliest_collection?: string | null
  earliest_delivery?: string | null
}

/**
 * Whether a linked card must ask the supplier again at this quantity — the
 * same re-check the detail page makes. Needed when the quantity is above the
 * branch stock and the supplier gave no warehouse date (Wolseley / UKPS only
 * date the warehouse for a quantity above the branch), or above what the
 * method can supply where the backorder date is the supplier's own quote
 * (Wolseley).
 *
 * @param listing - The card's listing at its branch.
 * @param method - The selected collection / delivery method.
 * @param quantity - What the cart line will hold after the add.
 */
export function quickAddNeedsQuantityCheck(
  listing: BranchStockListing,
  method: CartFulfillmentMethod,
  quantity: number | null,
): boolean {
  if (quantity === null || quantity <= 0) return false
  const { branchQty, depotQty, capacityQty } = quickAddMethodStock(listing, method)
  const depotDate = method === 'pickup' ? listing.pickupDepotDate : listing.deliveryDepotDate
  const needsDepotDate =
    (depotQty ?? 0) > 0 && depotDate == null && quantity > (branchQty ?? 0)
  const needsBackorderDate =
    listing.backorderAllowed && listing.backorderDateFromApi && quantity > capacityQty
  return needsDepotDate || needsBackorderDate
}

/**
 * The listing with the supplier's answer for the engineer's quantity laid
 * over it, as the detail page does: the warehouse dates it now gives, and —
 * when the backorder date is the supplier's own quote — that quote for this
 * quantity. No backorder date in the answer means no backorder at this
 * quantity (the rule on every other screen).
 *
 * @param listing - The card's listing from the search.
 * @param checked - The quantity check's item for this product.
 */
export function withQuantityCheck(
  listing: BranchStockListing,
  checked: QuickAddQuantityCheckItem,
): BranchStockListing {
  return {
    ...listing,
    pickupDepotDate: checked.pickup_dc_date ?? listing.pickupDepotDate,
    deliveryDepotDate: checked.delivery_dc_date ?? listing.deliveryDepotDate,
    ...(listing.backorderDateFromApi
      ? {
          backorderCollectionDate: listing.canCollect
            ? checked.earliest_collection ?? null
            : null,
          backorderDeliveryDate: listing.canDeliver ? checked.earliest_delivery ?? null : null,
        }
      : {}),
  }
}
